import { Injectable } from '@angular/core';
import { Position } from '@capacitor/geolocation';
import { Driver } from '@core/models/Driver';
import { Reply } from '@core/models/reply';
import { RouteResource } from '@core/models/RouteResource';
import { ConfigurationService, SERVICES } from '@core/models/Session';
import { Unit } from '@core/models/Unit';
import { ApiGatewayRoutesService } from '@core/services/api.gateway-routes.service';
import { BaseEntitiesService } from '@core/services/entities-services/base-entities.service';
import { TasksService } from '@core/services/entities-services/tasks.service';
import { GpsService } from '@core/services/gps.service';
import { SessionService } from '@core/services/session.service';
import { ToastController } from '@ionic/angular';
import { BehaviorSubject, lastValueFrom, Subject, takeUntil, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Route } from '../../../routes/models/Route';
import { ROUTE_EVENT } from '../../../routes/models/ROUTE_EVENT';
import { RouteReply } from '../../../routes/models/RouteReply';
import { RouteTemplateStatus } from '../../../routes/models/RouteTemplate';
import { Task } from '../../../routes/models/Task';
import { TaskStatus } from '../../../routes/models/TaskStatus';
@Injectable({ providedIn: 'root' })
export class RoutesV3Service extends BaseEntitiesService<Route> {
  public moduleStarted: boolean = false;
  protected readonly _pendingCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly pendingCount = this._pendingCount.asObservable();
  protected readonly _moduleVersion: BehaviorSubject<('v1' | 'v2' | 'v3')> = new BehaviorSubject<('v1' | 'v2' | 'v3')>('v3');
  public readonly moduleVersion = this._moduleVersion.asObservable();
  private editSelectedElement: boolean = true;
  private readonly _configModule: BehaviorSubject<ConfigurationService> = new BehaviorSubject<ConfigurationService>(null);
  public readonly configModule = this._configModule.asObservable();
  private readonly _routesByLocation: BehaviorSubject<Route[]> = new BehaviorSubject<Route[]>([]);
  public readonly routesByLocation = this._routesByLocation.asObservable();
  private readonly _startedRoutes: BehaviorSubject<Route[]> = new BehaviorSubject<Route[]>([]);
  public readonly startedRoutes = this._startedRoutes.asObservable();
  private readonly _loadingRoutes: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly loadingRoutes = this._loadingRoutes.asObservable();
  private readonly _loadingRoutesByLocation: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly loadingRoutesByLocation = this._loadingRoutesByLocation.asObservable();
  private currentPosition: Position = null;
  private cancelPreviousRequests = new Subject<void>();
  private showRoutesSelector: boolean = false;
  constructor(private _apiRoutes: ApiGatewayRoutesService, override _toast: ToastController, override _session: SessionService, private _gps: GpsService, private _serviceTasks: TasksService) {
    _session.logout$.subscribe(() => this.resetService());
    super(_apiRoutes, _toast, _session);
    this._gps.startWatch().subscribe(async position => {
      this.currentPosition = position;
    });
  }
  // Routes Selector
  public setShowRoutesSelector(state: boolean) {
    this.showRoutesSelector = state;
  }
  isSelectorShowing(): boolean {
    return this.showRoutesSelector;
  }
  // Selected element
  public selectElement(element: Route): void {
    this._selected.next(element);
  }
  public setIsSelectedElementEditable(editable: boolean) {
    this.editSelectedElement = editable;
  }
  public isSelectedElementEditable(): boolean {
    return this.editSelectedElement;
  }
  public getSelectedElement(): Route {
    return this._selected.getValue();
  }
  // Repository
  public add(element: Route[] | Route): Promise<Route> {
    throw new Error('Not implemented');
  }
  public remove(elements: Route[]): Promise<void> {
    throw new Error('Not implemented');
  }
  public async asignResource(ids: string[], resource: RouteResource): Promise<boolean> {
    try {
      this._routesByLocation.next(this._routesByLocation.getValue().filter(route => !ids.some(id => id === route.id)));
      await lastValueFrom(this._apiRoutes.put('/v2/routes/resource/asign', { ids, resource }, false));
      await this.loadElements(false);
      return true;
    } catch (e) {
      const toast = await this._toast.create({
        animated: true,
        header: 'Error al asignar las rutas a la unidad ' + resource.name,
        duration: 3000,
      });
      await toast.present();
      return false;
    }
  }
  public async unasignResource(ids: string[]): Promise<boolean> {
    try {
      this._loadingRoutesByLocation.next(true);
      this._elements.next(this._elements.getValue().filter(route => !ids.some(id => id === route.id)));
      await lastValueFrom(this._apiRoutes.put('/v2/routes/resource/unasign', { ids }, false));
      await this.loadRoutesByLocation(true);
      this._loadingRoutesByLocation.next(false);
      return true;
    } catch (e) {
      const toast = await this._toast.create({
        animated: true,
        header: 'Error al desasignar la ruta de la unidad ',
        duration: 3000,
      });
      await toast.present();
      return false;
    }
  }
  public update(element: Route): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        await lastValueFrom(this._apiRoutes.put('/v2/routes', element, false));
        return resolve(true);
      } catch (e) {
        return reject(e);
      }
    });
  }
  override loadElements(showLoading?: boolean): Promise<Route[]> {
    return new Promise<Route[]>(async (resolve, reject) => {
      try {
        if (showLoading) {
          this._loadingRoutes.next(true);
        }
        const session = this._session.getSession();
        const filters = this.getFiltersSnapshot();
        const pagination = this.getPaginationSnapshot();
        const interval = filters['date'].data;
        const status = filters['status'] ? filters['status'].data : null;
        const drivers = [session.driver.id];
        const units = session.unit ? [session.unit.id] : null;
        const subscription = this._apiRoutes.get(`/v2/routes`, false, false, {
          fields: JSON.stringify([]),
          page: pagination.page,
          pageSize: pagination.pageSize,
          sort: JSON.stringify(pagination.sort),
          between: [
            JSON.stringify({ field: 'initDate', value1: new Date(interval[0]).setHours(0, 0, 0, 0), value2: new Date(interval[1]).setHours(23, 59, 59, 59) }),
            JSON.stringify({ field: 'endDate', value1: new Date(interval[0]).setHours(0, 0, 0, 0), value2: new Date(interval[1]).setHours(23, 59, 59, 59) }),
          ],
          filters: JSON.stringify({
            ...(drivers && { drivers: drivers }),
            ...(units && { units: units }),
            ...(status && { status: status }),
          }),
        })
          .pipe(
            takeUntil(this.cancelPreviousRequests),
            catchError(async (e: any) => {
              const toast = await this._toast.create({
                animated: true,
                header: 'Error al obtener las rutas',
                duration: 5000,
              });
              await toast.present();
              return throwError(e);
            }),
          )
          .subscribe({
            next: (response) => {
              this._elements.next(response.data);
              this.setPagination({ ...this.getPaginationSnapshot(), totalElements: response.total });
              if (showLoading) {
                this._loadingRoutes.next(false);
              }
              resolve(response.data);
            },
            error: reject,
          });
        return () => subscription.unsubscribe();
      } catch (e: any) {
        console.log(`Error al obtener las rutas ${e}`);
        return resolve([]);
      }
    });
  }
  public addReplyToTask(taskId: string, reply: Reply): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        await lastValueFrom(this._apiRoutes.post(`/v2/tasks/reply`, { taskId, reply }, false));
        return resolve(true);
      } catch (e: any) {
        console.log(e);
        return resolve(false);
      }
    });
  }
  public changeTaskStatus(status: TaskStatus, taskId: string, routeId: string, position: Position): Promise<Task> {
    return new Promise<Task>(async (resolve, reject) => {
      try {
        const taskUpdated = await lastValueFrom(this._apiRoutes.put(`/v2/routes/task/status`, {
          taskId,
          routeId,
          status,
          location: { lat: position.coords.latitude, lon: position.coords.longitude },
        }, false));
        if (taskUpdated) {
          this._serviceTasks.selectElement(taskUpdated);
          const routes = await this.loadElements();
          const routeIndex = routes.findIndex(r => r.id === taskUpdated.route.id);
          if (routeIndex !== -1) {
            this.selectElement(routes[routeIndex]);
          }
        }
        return resolve(taskUpdated);
      } catch (e: any) {
        console.log(e);
        return resolve(null);
      }
    });
  }
  public changeStatus(routeId: string, routeTemplateId: string, status: RouteTemplateStatus, position: Position, reply: RouteReply): Promise<Route> {
    return new Promise(async (resolve, reject) => {
      try {
        const routeUpdated = await lastValueFrom<Route>(this._apiRoutes.put(`/v2/routes/status`, {
          routeId,
          routeTemplateId,
          routeTemplateStatus: status,
          location: { lat: position.coords.latitude, lon: position.coords.longitude },
          reply: reply,
        }, false));
        if (routeUpdated) {
          this.selectElement(routeUpdated);
        }
        const routes = this.getElements();
        const index = routes.findIndex(route => route.id === routeUpdated.id);
        if (index !== -1) {
          routes[index] = routeUpdated;
          this._elements.next(routes);
        }
        return resolve(routeUpdated);
      } catch (e: any) {
        console.log(e);
        return resolve(null);
      }
    });
  }
  public async loadRoutesByLocation(showLoader: boolean): Promise<void> {
    try {
      if (!this.currentPosition) {
        return;
      }
      this._loadingRoutesByLocation.next(showLoader);
      const config = this._configModule.getValue();
      if (!config) {
        return;
      }
      const response = await lastValueFrom<Route[]>(this._apiRoutes.post(`/v2/routes/read/location`, {
        lat: this.currentPosition.coords.latitude,
        lon: this.currentPosition.coords.longitude,
        radioMts: config.FIND_ROUTES_RADIO_MTS || 1000,
      }, false));
      this._routesByLocation.next(response);
      this._loadingRoutesByLocation.next(showLoader ? false : showLoader);
    } catch (e: any) {
      console.log(e);
      console.log('Error al obtener rutas segun la ubicacion');
    }
  }
  public readOne(id: string): Promise<Route> {
    return new Promise(async (resolve, reject) => {
      try {
        const route = await lastValueFrom(this._apiRoutes.get(`/v2/routes/${id}`, false));
        return resolve(route);
      } catch (e: any) {
        console.log(e);
        return resolve(null);
      }
    });
  }
  public async removePackage(taskId: string, routeId: string, packageId: string, unit: Unit, driver: Driver, position: Position) {
    try {
      return await lastValueFrom<Task>(this._apiRoutes.post(`/v2/routes/task/package/delete`, {
        taskId: taskId,
        routeId: routeId,
        packageId: packageId,
        unit: unit,
        driver: driver,
        location: { lat: position.coords.latitude, lon: position.coords.longitude, address: '', isGeofence: false },
      }, true));
    } catch (e: any) {
      console.log(e);
      const toast = await this._toast.create({ color: 'danger', message: `Error al eliminar bulto: ${e.message}` });
      await toast.present();
      return null;
    }
  }
  public async checkPackage(taskId: string, routeId: string, packageId: string, loaded: boolean) {
    try {
      return await lastValueFrom<Task>(this._apiRoutes.put(`/v2/routes/task/package/load`, { taskId, routeId, packageId, loaded }, false));
    } catch (e: any) {
      console.log(e);
      const toast = await this._toast.create({ color: 'danger', message: `Error al cambiar el estado del bulto: ${e.message}` });
      await toast.present();
      return null;
    }
  }
  public getStartedRoutesByUnit() {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const unit = await this._session.getUnit();
        if (unit) {
          const routes = await lastValueFrom(this._apiRoutes.get(`/v2/routes/started/unit/${unit.id}`, false));
          this._startedRoutes.next(routes);
        }
        return resolve(true);
      } catch (e: any) {
        console.log(e);
        return resolve(false);
      }
    });
  }
  public sendEventOpenRoute(routeId: string, position: Position) {
    return new Promise(async () => {
      try {
        await lastValueFrom(this._apiRoutes.post(`/v2/routes/events`, {
          eventType: ROUTE_EVENT.ROUTE_OPENED,
          routeId,
          location: { latitude: position.coords.latitude, longitude: position.coords.longitude },
        }, false));
      } catch (e) {
        console.log(`Error al enviar el evento de apertura: ${e}`);
      }
    });
  }
  public resetService() {
    this._configModule.next(null);
    this._elements.next([]);
    this._routesByLocation.next([]);
    this._startedRoutes.next([]);
    this._loadingRoutes.next(false);
    this._loadingRoutesByLocation.next(false);
    this.resetFilters();
    this.moduleStarted = false;
    this.showRoutesSelector = false;
  }
  public async findAndCheckPackage(code: string, unit: Unit, driver: Driver, position: Position) {
    return await lastValueFrom<{ message: string, task: Task }>(this._apiRoutes.put('/v2/routes/task/package/find/load', {
      code: code,
      unit: unit,
      driver: driver,
      location: { lat: position.coords.latitude, lon: position.coords.longitude, address: '', isGeofence: false },
    }));
  }
  async loadConfigModule() {
    const session = this._session.getSession();
    const index = session.services?.findIndex((service: any) => service.name === SERVICES.ASSISTANT_ROUTES);
    if (index && index !== -1) {
      const config = session.services[index].config;
      this._configModule.next(config);
      await this.loadElements(true);
      if (config?.FIND_ROUTES_BY_LOCATION) {
        await this.loadRoutesByLocation(false);
      }
    }
  }
}
